import { PageTitleRow } from "../../components/layout/PageTitleRow";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Building2, RefreshCw, Search } from "lucide-react";
import { AppLayout, PageBackLink } from "../../components/layout";
import { ContentCard, DataTable, EmptyState, LoadingState, ui } from "../../components/ui";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { TablePagination } from "../leave-management/components/TablePagination";
import { cn } from "../../lib/utils";
import {
  fetchTenantRequests,
  fetchTenantRequestsByStatus,
  updateTenantRequestStatus,
} from "../../services/tenantRequestService";
import {
  LIST_FIELDS,
  SEARCH_FIELD,
  TENANT_REQUEST_STATUSES,
  fieldLabel,
  fieldValue,
  statusLabel,
  tenantRequestId,
  tenantRequestStatusOf,
} from "./tenantRequestFields";
import { TenantRequestStatusBadge } from "./TenantRequestStatusBadge";
import { TenantRequestDetailsDialog } from "./TenantRequestDetailsDialog";

const PAGE_SIZE = 10;
const ALL = "all";

/**
 * Tenant requests — organizations asking to be onboarded.
 *
 * The request is raised from outside the product, by someone with no account,
 * so this screen is the first time anybody here sees it. It reads the queue,
 * shows one request in full, and moves it along; it never creates or completes
 * one.
 */
export function TenantRequestsPage() {
  const { t } = useTranslation();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  /** The name being searched for. It narrows what is on screen, not a read. */
  const [name, setName] = useState("");

  const [status, setStatus] = useState("Open");
  const [page, setPage] = useState(1);

  /** The request whose card is open. */
  const [opened, setOpened] = useState(null);

  /** Which request is being moved, and what went wrong. */
  const [updatingId, setUpdatingId] = useState(null);
  const [updateError, setUpdateError] = useState("");

  /**
   * The read.
   *
   * The status is the only thing the API narrows by; its search answers one
   * plain array of every request, with no page envelope to read a total from.
   * So the whole queue is what arrives, and the name and the page are applied
   * to it here rather than asked for.
   */
  const load = useCallback(() => {
    setLoading(true);
    setLoadError("");

    const read =
      status !== ALL ? fetchTenantRequestsByStatus(status) : fetchTenantRequests();

    read
      .then((result) => setRows(result.rows))
      .catch((error) => {
        console.error("Failed to load tenant requests:", error);
        setRows([]);
        setLoadError(error.message || t("tenantRequests.errorLoad"));
      })
      .finally(() => setLoading(false));
  }, [status, t]);

  useEffect(() => load(), [load]);

  /** The name, matched against the field the list is searched on. */
  const matched = useMemo(() => {
    const wanted = name.trim().toLowerCase();
    if (!wanted) return rows;
    return rows.filter((request) =>
      fieldValue(request, SEARCH_FIELD).toLowerCase().includes(wanted)
    );
  }, [rows, name]);

  const totalPages = Math.max(1, Math.ceil(matched.length / PAGE_SIZE));
  const pagedRows = matched.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  /** A narrower list is a shorter one: page 4 of it may no longer exist. */
  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  const applyStatus = (value) => {
    setStatus(value);
    setPage(1);
  };

  const changeStatus = async (request, value, details) => {
    const id = tenantRequestId(request);
    setUpdatingId(id);
    setUpdateError("");
    try {
      await updateTenantRequestStatus(id, value, details);
      // Close after a successful update for every status.
      setOpened((current) => (current === request ? null : current));
      load();
    } catch (error) {
      console.error("Failed to update the tenant request status:", error);
      setUpdateError(error.message || t("tenantRequests.errorUpdate"));
    } finally {
      setUpdatingId(null);
    }
  };

  const columns = [
    ...LIST_FIELDS.map((key) => ({ key, label: fieldLabel(key) })),
    { key: "status", label: t("status.status") },
  ];

  return (
    <AppLayout>

      <div className="mb-5">
        <PageTitleRow back={<PageBackLink to="/" />}>
          <h1 className={ui.pageTitle}>{t("tenantRequests.title")}</h1>
        </PageTitleRow>
        
      </div>

      {loadError ? (
        <div className="mb-4 flex flex-col gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm font-semibold text-rose-700">{loadError}</p>
          <button
            type="button"
            onClick={load}
            className="inline-flex h-8 shrink-0 items-center gap-1.5 self-start rounded-lg border border-rose-300 bg-white px-3 text-[11px] font-bold text-rose-700 transition hover:bg-rose-100 sm:self-auto"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            {t("tenantRequests.retry")}
          </button>
        </div>
      ) : null}

      <div className="mb-4 grid grid-cols-1 gap-2.5 sm:flex sm:flex-wrap sm:items-center">
        {/* Typed-through: it narrows the list already in hand, so there is
            nothing to submit and no request to spare. */}
        <div className="relative w-full sm:w-[280px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 per-file-hint" />
          <input data-page-search="true"
            type="search"
            value={name}
            placeholder={t("tenantRequests.searchPlaceholder")}
            aria-label={t("tenantRequests.searchPlaceholder")}
            onChange={(event) => {
              setName(event.target.value);
              setPage(1);
            }}
            className={cn(ui.input, "h-9 w-full pl-9")}
          />
        </div>

        <div className="w-full sm:w-[190px]">
          <Select value={status} onValueChange={applyStatus}>
            <SelectTrigger className="h-9 w-full rounded-lg border border-(--color-text-border) bg-white text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-60 overflow-y-auto">
              <SelectItem value={ALL}>{t("common.allStatuses")}</SelectItem>
              {TENANT_REQUEST_STATUSES.map((value) => (
                <SelectItem key={value} value={value}>
                  {statusLabel(value)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <ContentCard>
        {loading ? (
          <LoadingState message={t("tenantRequests.loading")} />
        ) : matched.length === 0 ? (
          <EmptyState
            icon={Building2}
            title={t("tenantRequests.emptyTitle")}
            message={t("tenantRequests.emptyMessage")}
          />
        ) : (
          <>
            <DataTable columns={columns} minWidth="820px">
              {pagedRows.map((request) => (
                <tr
                  key={tenantRequestId(request)}
                  onClick={() => setOpened(request)}
                  className="cursor-pointer transition hover:bg-workspace/20"
                >
                  {LIST_FIELDS.map((key, index) => (
                    <td
                      key={key}
                      className={cn(
                        "px-4 py-3",
                        index === 0
                          ? "font-semibold text-foreground"
                          : "per-file-hint"
                      )}
                    >
                      {fieldValue(request, key) || "—"}
                    </td>
                  ))}
                  <td className="px-4 py-3">
                    <TenantRequestStatusBadge status={tenantRequestStatusOf(request)} />
                  </td>
                </tr>
              ))}
            </DataTable>

            {/* Mobile cards — the same few fields the table shows. */}
            <div className="divide-y divide-(--color-text-border)/40 md:hidden">
              {pagedRows.map((request) => (
                <div
                  key={tenantRequestId(request)}
                  onClick={() => setOpened(request)}
                  className="cursor-pointer p-4 transition hover:bg-workspace/20"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      {LIST_FIELDS.map((key, index) => (
                        <p
                          key={key}
                          className={cn(
                            "truncate",
                            index === 0
                              ? "text-sm font-bold text-foreground"
                              : "mt-0.5 text-[12px] per-file-hint"
                          )}
                        >
                          {fieldValue(request, key) || "—"}
                        </p>
                      ))}
                    </div>
                    <TenantRequestStatusBadge status={tenantRequestStatusOf(request)} />
                  </div>
                </div>
              ))}
            </div>

            <TablePagination
              page={page}
              totalPages={totalPages}
              totalItems={matched.length}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
            />
          </>
        )}
      </ContentCard>

      <TenantRequestDetailsDialog
        request={opened}
        busy={updatingId === tenantRequestId(opened)}
        error={updateError}
        onChangeStatus={(value, details) => changeStatus(opened, value, details)}
        onOpenChange={(open) => {
          if (!open) {
            setOpened(null);
            setUpdateError("");
          }
        }}
      />
    </AppLayout>
  );
}

export default TenantRequestsPage;
